import { aiAgentApi } from '@texas/api/endpoints/chat/aiAgentApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { RichTextChunk } from '@texas/api/endpoints/chat/richTextTypes';
import {
  InlineFile,
  UnionChatMessage,
} from '@texas/api/endpoints/chat/messageTypes';
import { JobRecord } from '@texas/api/endpoints/jobRecordsApi';
import {
  ChatItem,
  ChatItemType,
} from '@texas/api/endpoints/chat/aiMessageTypes';
import { randomSeed } from '@texas/resources/constants';
import { chatApi } from '@texas/api/endpoints/chat/chatApi';

interface Props {
  threadId: number;
  poll: boolean;
}

export function useAgentSession(props: Props) {
  const promptReq = useApiRequest(aiAgentApi.generalPrompt);

  const [history, setHistory] = useState<{
    messages: ChatItem[];
    scroll: ScrollBehavior | undefined;
  }>({ scroll: undefined, messages: [] });

  const historyRef = useRef<string[]>([]);
  const messages = useRef<Record<string, UnionChatMessage>>({});
  const jobs = useRef<Record<string, JobRecord>>({});

  const { refetch, loading, data } = useApiResource(chatApi.getSession);

  useEffect(() => {
    refetch(props.threadId);

    if (!props.poll) {
      return;
    }

    const interval = setInterval(() => {
      chatApi.pollSession(props.threadId).then((response) => {
        if (!response.hasError && response.data.items.length > 0) {
          messages.current = {
            ...messages.current,
            ...response.data.chatMessages,
          };
          jobs.current = {
            ...jobs.current,
            ...response.data.jobs,
          };
          const newItems = response.data.items.filter(
            (i) => !historyRef.current.includes(`${i.type}-${i.id}`),
          );
          setHistory((s) => ({
            scroll: undefined,
            messages: [...s.messages, ...newItems],
          }));

          historyRef.current = [
            ...historyRef.current,
            ...newItems.map((m) => `${m.type}-${m.id}`),
          ];
        }
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [props.poll, props.threadId, refetch]);

  useEffect(() => {
    if (!data) {
      return;
    }
    messages.current = data.chatItems.chatMessages;
    jobs.current = data.chatItems.jobs;
    historyRef.current = data.chatItems.items.map((m) => `${m.type}-${m.id}`);

    setHistory({ messages: data.chatItems.items, scroll: 'auto' });
  }, [data]);

  const prompt = useCallback(
    async (
      userId: number,
      val: RichTextChunk[],
      files: InlineFile[],
      rawString: string,
    ) => {
      const r = Math.floor(Math.random() * randomSeed);
      messages.current = {
        ...messages.current,
        [r]: {
          richTextChunks: val,
          files: files,
          role: 'User',
          rawMessage: '',
          userId: userId,
        },
      };

      setHistory((s) => ({
        scroll: 'smooth',
        messages: [
          ...s.messages,
          {
            index: s.messages.length,
            type: ChatItemType.Message,
            id: r,
          },
        ],
      }));

      await promptReq.request({
        chunks: val,
        threadId: props.threadId,
        fileIds: files.map((f) => f.fileId),
        rawString: rawString,
      });
    },
    [promptReq, props.threadId],
  );

  return {
    prompt,
    history,
    threadLoading: loading,
    messages,
    jobs,
  };
}

export const AiAgentContext = createContext<ReturnType<
  typeof useAgentSession
> | null>(null);
